import React from "react";
import {
  Box,
  Typography,
  Stack,
  Tooltip,
  IconButton,
  Chip,
  Divider,
  CardContent,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import EventIcon from "@mui/icons-material/Event";
import BusinessIcon from "@mui/icons-material/Business";

import { StyledCard } from "../common/StyledCard";
import { TimeslotResponseDto } from "../../shared/dtos/timeslot/TimeslotResponseDto";

interface TimeslotCardProps {
  timeslot: TimeslotResponseDto;
  onEdit?: () => void;
  onDelete?: () => void;
}

const formatTime = (timeString?: string | null): string => {
  if (!timeString) return "N/A";
  const [hour, minute] = timeString.split(":");
  const date = new Date();
  date.setHours(parseInt(hour, 10), parseInt(minute, 10));
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const TimeslotCard: React.FC<TimeslotCardProps> = ({
  timeslot,
  onEdit,
  onDelete,
}) => {
  const dayName = timeslot.dayOfWeek
    ? timeslot.dayOfWeek.charAt(0) + timeslot.dayOfWeek.slice(1).toLowerCase()
    : "Unspecified";

  return (
    <StyledCard>
      <CardContent
        sx={{ p: 2, display: "flex", flexDirection: "column", height: "100%" }}
      >
        {/* Header */}
        <Stack
          direction="row"
          sx={{
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1,
          }}
        >
          <Box>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <EventIcon fontSize="small" sx={{ color: "text.secondary" }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {dayName}
              </Typography>
            </Stack>
            <Chip
              label={`${formatTime(timeslot.startTime)} - ${formatTime(timeslot.endTime)}`}
              size="small"
              color="secondary"
              variant="outlined"
              icon={<AccessTimeIcon fontSize="small" />}
              sx={{
                mt: 1,
                height: 24,
                fontSize: "0.75rem",
                fontWeight: 700,
              }}
            />
          </Box>
          <Stack direction="row">
            {onEdit && (
              <Tooltip title="Edit Timeslot">
                <IconButton size="small" color="secondary" onClick={onEdit}>
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {onDelete && (
              <Tooltip title="Delete Timeslot">
                <IconButton size="small" color="error" onClick={onDelete}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        {/* Footer Details */}
        <Box sx={{ flexGrow: 1 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <BusinessIcon fontSize="small" sx={{ color: "action.active" }} />
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              <b>Branch:</b> {timeslot.branchName || "Main Campus"}
            </Typography>
          </Stack>
        </Box>
      </CardContent>
    </StyledCard>
  );
};

export default TimeslotCard;
